import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  Breadcrumbs,
  Link,
  Tooltip,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { OrderItemVm } from 'api/picking/order-item.vm';
import { OrdersStatus, OrderVm } from 'api/picking/order.vm';
import { GetOrderItemsVm } from 'api/picking/get-order-items.vm';
import { getOrderItems } from 'api/picking/get-order-items';
import { ArrowBack, CheckCircle, Error, Pending } from '@mui/icons-material';
import { getOrder } from 'api/picking/get-order';
import { GetOrderVm } from 'api/picking/get-order.vm';

const SalesOrderPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { orderId } = useParams<{ orderId: string }>();
  const [orderItems, setOrderItems] = useState<OrderItemVm[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [nextOffset, setNextOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [orderDetails, setOrderDetails] = useState<OrderVm | null>(null);

  const handleBackNavigation = () => {
    navigate(-1);
  };

  const fetchOrderItems = (offset: string | null, reset: boolean = false) => {
    if (!orderId) return;

    setLoading(true);

    getOrderItems(orderId, offset || '', {
      success: (response: GetOrderItemsVm) => {
        setHasMore(response.nextOffset !== null && response.nextOffset !== '');
        setNextOffset(response.nextOffset);
        setOrderItems(reset ? response.objects : [...orderItems, ...response.objects]);
        setLoading(false);
      },
      error: (err) => {
        console.error('Falha ao obter itens da encomenda:', err);
        setLoading(false);
      },
    });
  };

  const fetchOrderDetails = () => {
    if (!orderId) return;

    setLoadingDetails(true);

    getOrder(orderId, {
      success: (response: GetOrderVm) => {
        setOrderDetails(response);
        setLoadingDetails(false);
      },
      error: (err) => {
        console.error('Falha ao obter detalhes da encomenda:', err);
        setLoadingDetails(false);
      },
    });
  };

  useEffect(() => {
    fetchOrderItems(null, true);
    fetchOrderDetails();
  }, [orderId]);

  const handleLoadMore = () => {
    if (!hasMore || !nextOffset) return;
    fetchOrderItems(nextOffset);
  };

  const getStatusChip = (item: OrderItemVm) => {
    if (item.isPending) {
      return <Chip icon={<Pending fontSize="small" />} label="Pendente" color="info" size="small" />;
    }
    return <Chip icon={<CheckCircle fontSize="small" />} label="Concluído" color="success" size="small" />;
  };

  const getOrderStatusChip = (status: OrdersStatus) => {
    switch (status) {
      case OrdersStatus.PENDING:
        return <Chip icon={<Pending fontSize="small" />} label="Pendente" color="info" size="small" />;
      case OrdersStatus.CLOSED:
        return <Chip icon={<Error fontSize="small" />} label="Fechada" color="error" size="small" />;
      default:
        return <Chip label="Desconhecido" size="small" />;
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <Breadcrumbs aria-label="breadcrumb">
          <Link underline="hover" color="inherit" onClick={handleBackNavigation}>
            Encomendas de Cliente
          </Link>
          <Typography color="text.primary">Encomenda {orderId}</Typography>
        </Breadcrumbs>
      </Box>

      {loadingDetails ? (
        <CircularProgress />
      ) : orderDetails ? (
        <Paper sx={{ mb: 3, p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Detalhes da Encomenda
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                ID
              </Typography>
              <Typography>{orderDetails.id}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Circuito ID
              </Typography>
              <Typography>{orderDetails.circuitId}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Circuito
              </Typography>
              <Typography>{orderDetails.circuitName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Cliente
              </Typography>
              <Typography>{orderDetails.clientName}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Criada Em
              </Typography>
              <Typography>{new Date(orderDetails.createdAt).toLocaleDateString('pt-PT')}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Estado
              </Typography>
              <Typography>{getOrderStatusChip(orderDetails.status)}</Typography>
            </Box>
          </Box>
        </Paper>
      ) : (
        <Typography variant="body2" color="error">
          Falha ao carregar detalhes da encomenda
        </Typography>
      )}

      <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
        Itens da Encomenda
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead sx={{ backgroundColor: theme.palette.grey[800] }}>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Produto</TableCell>
              <TableCell>SKU</TableCell>
              <TableCell>Armazém</TableCell>
              <TableCell>Quantidade</TableCell>
              <TableCell>Recolhido</TableCell>
              <TableCell>Satisfeito</TableCell>
              <TableCell>Estado</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {orderItems.map((item) => (
              <TableRow key={`${item.orderId}-${item.productId}`} hover>
                <TableCell>{item.id}</TableCell>
                <TableCell>{item.productName}</TableCell>
                <TableCell>{item.productSku}</TableCell>
                <TableCell>{item.warehouseId}</TableCell>
                <TableCell>{item.quantity}</TableCell>
                <TableCell>{item.quantityPicked}</TableCell>
                <TableCell>{item.quantitySatisfied}</TableCell>
                <TableCell>{getStatusChip(item)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        {loading ? (
          <CircularProgress />
        ) : hasMore ? (
          <Button variant="outlined" onClick={handleLoadMore} disabled={loading || !nextOffset}>
            Carregar mais
          </Button>
        ) : orderItems.length > 0 ? (
          <Typography variant="body2" color="textSecondary">
            Não existem mais itens para carregar
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Nenhum item encontrado
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default SalesOrderPage;
